import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Plus, Edit, Trash2, Save, X, Search } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { isAdmin } from "../../utils/roles";
import {
  fetchCategoryTypes,
  createCategoryType,
  updateCategoryType,
  deleteCategoryType,
} from "../../api/CatalogAPIs";
import {
  CatalogShell,
  ConfirmDialog,
  TableSkeleton,
  EmptyState,
  Switch,
  parseBilingual,
  joinBilingual,
  displayAr,
  displayEn,
} from "./catalogUi";

const emptyForm = { nameAr: "", nameEn: "", code: "", isActive: true };

const CategoryTypesPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canManage = isAdmin(authState.role);

  const [search, setSearch] = useState("");
  const [formMode, setFormMode] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { data = [], isLoading, error, refetch } = useQuery({
    queryKey: ["catalogCategoryTypes"],
    queryFn: fetchCategoryTypes,
    enabled: canManage,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!authState.isValidating && !canManage) navigate("/login");
  }, [authState, canManage, navigate]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter((item) => {
      const names = `${item.name || ""} ${item.code || ""} ${displayAr(item.name)} ${displayEn(item.name)}`.toLowerCase();
      return names.includes(term);
    });
  }, [data, search]);

  const openCreate = () => {
    setFormMode("create");
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (item) => {
    const names = parseBilingual(item.name);
    setFormMode("edit");
    setEditing(item);
    setForm({
      nameAr: names.ar,
      nameEn: names.en,
      code: item.code || "",
      isActive: item.isActive !== false,
    });
  };

  const closeForm = () => {
    setFormMode(null);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = joinBilingual(form.nameAr, form.nameEn);
    if (!name) {
      toast.error("الاسم مطلوب");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        code: form.code.trim() ? form.code.trim().toUpperCase() : null,
        isActive: form.isActive,
      };
      if (formMode === "create") {
        await createCategoryType(payload);
        toast.success("تم إنشاء نوع الفئة");
      } else {
        await updateCategoryType(editing.categoryTypeId, payload);
        toast.success("تم تحديث نوع الفئة");
      }
      closeForm();
      refetch();
    } catch (err) {
      toast.error(err.message || "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCategoryType(deleteTarget.categoryTypeId);
      toast.success("تم الحذف");
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      toast.error(err.message || "تعذر الحذف");
    } finally {
      setDeleting(false);
    }
  };

  if (!canManage) return null;

  return (
    <CatalogShell title="أنواع الفئات" subtitle="التصنيفات الرئيسية للكتالوج الجاهز">
      <ToastContainer position="top-center" />
      <div className="flex flex-col md:flex-row gap-3 justify-between mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute right-3 top-3.5 text-gray-400" size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو الكود..."
            className="w-full pr-10 pl-4 py-3 rounded-xl border border-gray-200 bg-white"
          />
        </div>
        <button
          onClick={openCreate}
          className="px-5 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold flex items-center gap-2 shadow"
        >
          <Plus size={18} /> نوع فئة جديد
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6">
          فشل تحميل أنواع الفئات. حاول مرة أخرى.
        </div>
      )}

      <div className="bg-white/90 rounded-2xl shadow border border-white/30 overflow-hidden">
        {isLoading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState text="لا توجد أنواع فئات مطابقة" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-right min-w-[720px]">
              <thead className="bg-gray-50 text-sm text-gray-600">
                <tr>
                  <th className="p-3">الاسم عربي</th>
                  <th className="p-3">الاسم إنجليزي</th>
                  <th className="p-3">الكود</th>
                  <th className="p-3">عدد المنتجات</th>
                  <th className="p-3">الحالة</th>
                  <th className="p-3">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const isPrinted = String(item.code || "").toUpperCase() === "PRINTED";
                  return (
                    <tr key={item.categoryTypeId} className="border-t border-gray-100">
                      <td className="p-3 font-medium">{displayAr(item.name)}</td>
                      <td className="p-3 text-gray-600">{displayEn(item.name)}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-sm">
                          {item.code || "—"}
                        </span>
                      </td>
                      <td className="p-3">{item.productCount ?? 0}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-sm ${
                            item.isActive ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {item.isActive ? "نشط" : "معطّل"}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEdit(item)}
                            className="p-2 rounded-lg bg-indigo-50 text-indigo-700"
                            title="تعديل"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(item)}
                            className="p-2 rounded-lg bg-red-50 text-red-700 disabled:opacity-40"
                            title={isPrinted ? "لا يمكن حذف نوع التصاميم المطبوعة" : "حذف"}
                            disabled={isPrinted}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formMode && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">
                {formMode === "create" ? "نوع فئة جديد" : "تعديل نوع الفئة"}
              </h3>
              <button onClick={closeForm}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">الاسم عربي</label>
                <input
                  value={form.nameAr}
                  onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">الاسم إنجليزي</label>
                <input
                  value={form.nameEn}
                  onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">الكود</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl uppercase"
                  disabled={String(editing?.code || "").toUpperCase() === "PRINTED"}
                />
              </div>
              <Switch
                checked={form.isActive}
                onChange={(checked) => setForm({ ...form, isActive: checked })}
                label="نشط"
              />
              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold flex items-center justify-center gap-2"
              >
                <Save size={16} /> {saving ? "جاري الحفظ..." : "حفظ"}
              </button>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="حذف نوع الفئة"
        message={`هل تريد حذف "${displayAr(deleteTarget?.name)}"؟ لا يمكن الحذف إذا كان مرتبطاً بفئات أو منتجات.`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </CatalogShell>
  );
};

export default CategoryTypesPage;
